import React from 'react';
import type { ConnectedProps } from 'react-redux';
import type { Dispatch } from 'redux';
import { bindActionCreators } from 'redux';
import debounce from 'lodash/debounce';
import type Luminary from '@lumen5/luminary';

import TextInput, {
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import {
    ALL_VIDEOS_VIEW_ID,
    clearVideos,
    getVideoSearchTerm,
    resetSearchedVideos,
    selectActiveView,
    selectActiveViewName,
    setLoadingDashboard,
    setVideoSearchTerm,
} from 'js/src/data/dashboard';
import type { NoOwnProps } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { bindLuminaryOperations, luminaryReduxConnect } from 'js/src/luminary';
import { loadVideosOperation } from 'js/src/luminary/dashboard-luminary-operations';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

type PropsFromRedux = ConnectedProps<typeof connector>;

const VideoSearchPresentation = React.memo(function VideoSearchPresentation({
    searchTerm,
    activeView,
    activeViewName,
    setVideoSearchTerm,
    clearVideos,
    loadVideos,
    resetSearchedVideos,
    setLoadingDashboard,
}: PropsFromRedux & NoOwnProps) {
    const handleSearchTrigger = (newSearchTerm: string) => {
        newSearchTerm = newSearchTerm.trim();
        if (newSearchTerm !== searchTerm) {
            if (!newSearchTerm) {
                resetSearchedVideos();
                loadVideos(1);
            } else {
                setVideoSearchTerm(newSearchTerm);
                setLoadingDashboard(true);
                clearVideos({
                    view: activeView,
                });
                debouncedSearchTermUpdate();
            }
        }
    };

    const handleOnChange = (evt: TypeInputChangeEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
        const newSearchTerm = evt.target.value;
        handleSearchTrigger(newSearchTerm);
    };

    const debouncedSearchTermUpdate = React.useMemo(() => {
        return debounce(
            () => {
                setLoadingDashboard(true);
                clearVideos({
                    view: activeView,
                });
                loadVideos(1);
            },
            500,
            {
                leading: true,
            },
        );
    }, [activeView, clearVideos, loadVideos, setLoadingDashboard]);

    const clearVideoSearchTerm = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        analytics.track('Dashboard Search Event', {
            activity: 'clear dashboard search query',
        });
        resetSearchedVideos();
        loadVideos(1);
    };

    const placeholder =
        activeView === ALL_VIDEOS_VIEW_ID
            ? 'Search all your videos'
            : `Search your ${activeViewName} videos`;
    return (
        <TextInput
            variant={TEXT_INPUT_VARIANTS.STANDARD}
            type="text"
            placeholder={placeholder}
            text={searchTerm}
            onChange={handleOnChange}
            iconLeft="search"
            showClear
            onClear={clearVideoSearchTerm}
            className="video-search"
        />
    );
});

const mapStateToProps = (state: RootState) => {
    return {
        searchTerm: getVideoSearchTerm(state),
        activeView: selectActiveView(state),
        activeViewName: selectActiveViewName(state),
    };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
    return bindActionCreators(
        {
            resetSearchedVideos,
            clearVideos,
            setLoadingDashboard,
            setVideoSearchTerm,
        },
        dispatch,
    );
};
const mapLuminaryOperationsToProps = (
    dispatch: Dispatch,
    getState: GetRootState,
    luminary: Luminary,
) => {
    return bindLuminaryOperations(
        {
            loadVideosOperation,
        },
        dispatch,
        getState,
        luminary,
    );
};
const connector = luminaryReduxConnect(
    mapStateToProps,
    mapDispatchToProps,
    undefined,
    mapLuminaryOperationsToProps,
);
const VideoSearch = connector(VideoSearchPresentation);

export default VideoSearch;
